// Change complexity as the reader shows it: the engine scores each change
// block (crates/engine/src/complexity.rs, docs/superpowers/specs/
// 2026-10-31-change-complexity-design.md); this module discounts the rows
// `detectMoves` found moved, which only the client knows, and words the
// figures for the block's mark, the file list and agents.
import type { Band, Block, Complexity, ComplexityFlag, Move, Row } from '../engine/types';

/** Upper score bounds of trivial, light and moderate; above is heavy. Mirrors the engine's `BANDS`. */
export const BANDS = [3, 8, 16] as const;

/** The band a score falls in. */
export function bandOf(score: number): Band {
  if (score <= BANDS[0]) return 'trivial';
  if (score <= BANDS[1]) return 'light';
  if (score <= BANDS[2]) return 'moderate';
  return 'heavy';
}

/** What each flag says, in words. */
export const FLAG_WORDS: Record<ComplexityFlag, string> = {
  api: 'changes a public API',
  errors: 'touches error handling',
  concurrency: 'touches concurrency',
  unsafe: 'unsafe code or raw HTML',
  security: 'touches auth, secrets or crypto',
  deletedTests: 'removes tests',
};

/**
 * `block`'s complexity with its moved rows discounted: each figure scaled
 * by the share of its changed rows not moved (a moved line was reviewed
 * where it came from), the band read again, and a reason saying so. An
 * approximation: the engine scored the block's logic as a whole, so a
 * branch on a moved line is discounted with its share, not by itself.
 * `isMoved` says which rows moved (`movedRows`; by default a row's own
 * `moveId`). Null for context blocks and blocks the engine did not score.
 */
export function effectiveComplexity(block: Block, isMoved: (row: Row) => boolean = (r) => !!r.moveId): Complexity | null {
  const c = block.complexity;
  if (!c) return null;
  let changed = 0;
  let moved = 0;
  for (const r of block.rows) {
    if (r.kind === 'context') continue;
    changed++;
    if (isMoved(r)) moved++;
  }
  if (!moved || !changed) return c;
  const share = (changed - moved) / changed;
  const scale = (n: number) => Math.round(n * share);
  const loose = scale(c.loose);
  const tight = c.tight === null ? null : scale(c.tight);
  const score = tight ?? loose;
  const reasons = [...c.reasons, `${moved} moved line${moved === 1 ? '' : 's'}: not scored`];
  return { ...c, loose, tight, score, band: bandOf(score), reasons, flags: share ? c.flags : [] };
}

/**
 * The mark's words: `label` its band; `text` the detail, naming both
 * figures (the tight one as parsed, the loose one as an estimate), the
 * reasons and the risks.
 */
export function complexityDetail(c: Complexity): { label: string; text: string } {
  const figures = c.tight === null ? `estimate ~${c.loose}` : `parsed ${c.tight} · estimate ~${c.loose}`;
  const risks = c.flags.map((f) => FLAG_WORDS[f]);
  const text = [`${capital(c.band)} to review`, figures, ...c.reasons, ...risks].join(' · ');
  return { label: c.band, text };
}

const capital = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** Which rows of the file at `path` `moves` moved: a removed row inside a move's source there, an added row inside its destination. */
export function movedRows(moves: readonly Move[], path: string): (row: Row) => boolean {
  const from = moves.filter((m) => m.from.path === path).map((m) => m.from);
  const to = moves.filter((m) => m.to.path === path).map((m) => m.to);
  if (!from.length && !to.length) return () => false;
  const inside = (ends: typeof from, line: number | null) => line !== null && ends.some((e) => e.startLine <= line && line <= e.endLine);
  return (r) => (r.kind === 'del' ? inside(from, r.old) : r.kind === 'add' ? inside(to, r.new) : false);
}

/** Per change block of `blocks`, its score after moves (`effectiveComplexity`); 0 where unscored. */
export function blockScores(blocks: readonly Block[], isMoved?: (row: Row) => boolean): number[] {
  return blocks.filter((b) => b.kind === 'change').map((b) => effectiveComplexity(b, isMoved)?.score ?? 0);
}
